import { useEffect, useState } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import useClubAthleteRosterData from '../../../state/clubAthleteRoster/UseClubAthleteRosterData';
import useClubRosterTransferData from '../../../state/clubRosterTransfer/UseClubRosterTransferData';
import useClubRosterVendorSyncData from '../../../state/clubRosterVendorSync/UseClubRosterVendorSyncData';

import useGlobalRoutesData from '@usas/ui-web/state/security/UseGlobalRoutesData';
import useBaseUsasClubData from '@usas/ui-web/state/clubBase/UseBaseUsasClubData';
import useOrgGroupTypesData from '@usas/ui-web/state/orgGroupTypes/UseOrgGroupTypesData';
import useGroupFieldNamesData from '@usas/ui-web/state/groupFieldNames/UseGroupFieldNamesData';
import useMemberPersistentStorage from '@usas/ui-web/state/memberPersistentStorage/UseMemberPersistentStorage';

import { DEFAULT_ID } from '@usas/ui-web/utils/Constants';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';
import { navigateToCrossUiRoute } from '@usas/ui-web/utils/FormatCrossUiRoute';

const INITIAL_STATE = {
  practiceGroupTypeId: DEFAULT_ID,
  siteGroupTypeId: DEFAULT_ID
};

const useClubAthleteRoster = () => {
  const navigate = useNavigate();
  const { getGlobalRoute } = useGlobalRoutesData();
  const { setPersistentStorage } = useMemberPersistentStorage();
  const { GROUP_CONSTANTS } = useGroupFieldNamesData();
  const { baseUsasClubState } = useBaseUsasClubData();
  const { orgGroupTypesState, getOrgGroupTypes } = useOrgGroupTypesData();
  const { clubAthleteRosterState, searchClubAthleteRoster } = useClubAthleteRosterData();
  const { clubRosterTransferState, postClubRosterTransfer } = useClubRosterTransferData();
  const { clubRosterVendorSyncState, setClubRosterVendorSyncState, postClubRosterVendorSync
  } = useClubRosterVendorSyncData();
  const [state, setState] = useState(INITIAL_STATE);

  const onEditAthlete = (personId) => {
    if (personId && baseUsasClubState.objData?.orgUnitId) {
      const returnUrl = getGlobalRoute('CLUB_ATHLETE_ROSTER')?.route;
      setPersistentStorage(
        personId,
        import.meta.env.VITE_PROJECT_NAME,
        returnUrl,
        'Back to Club'
      );

      const routeObj = getGlobalRoute('MEMBER_INFO');
      navigateToCrossUiRoute(routeObj?.uIProjectName, routeObj?.route, navigate);
    }
  };

  const onSubmitAthleteSync = (athletesToSync) => {
    if (Array.isArray(athletesToSync) && athletesToSync.length > 0) {
      const memberSyncArray = athletesToSync.map((athlete) => ({
        memberId: athlete.memberId,
        orgUnitId: baseUsasClubState.objData.orgUnitId
      }));

      postClubRosterVendorSync(memberSyncArray);
    }
  };

  const onSubmitAthleteTransfer = async (athletesToTransfer) => {
    if (Array.isArray(athletesToTransfer) && athletesToTransfer.length > 0) {
      const memberTransferArray = athletesToTransfer.map((athlete) => ({
        personOrgRoleDurationId: athlete.personOrgRoleDurationId,
        transferOrgUnitId: athlete.unClubId,
        transferSignatureDate: formatDate(new Date()),
        transferSignature: '',
        lastCompetitionDate: null
      }));

      await postClubRosterTransfer(memberTransferArray);

      onSubmitAthleteFilter(undefined);
    }
  };

  // If formState is undefined, reuse the last filter object
  const onSubmitAthleteFilter = (formState = undefined) => {
    if (baseUsasClubState.isObjLoaded === true) {
      searchClubAthleteRoster(baseUsasClubState.objData.usasClubId, formState ? createFilterObj(formState) : undefined);

      if (clubRosterVendorSyncState.isSaved === true) {
        setClubRosterVendorSyncState({
          ...clubRosterVendorSyncState,
          isSaving: false,
          isSaved: false,
          objData: {},
          message: '',
        });
      }
    }
  };

  useEffect(() => {
    if (baseUsasClubState.isObjLoaded === true) {
      searchClubAthleteRoster(baseUsasClubState.objData.usasClubId, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUsasClubState]);

  useEffect(() => {
    if (orgGroupTypesState.isArrayLoading === false) {
      if (orgGroupTypesState.isArrayLoaded === true) {
        const practiceGroupType = orgGroupTypesState.arrayData.find(
          (x) => x.typeName === GROUP_CONSTANTS.GROUP_TYPE_NAME_PRACTICE_GROUP
        );
        const siteGroupType = orgGroupTypesState.arrayData.find(
          (x) => x.typeName === GROUP_CONSTANTS.GROUP_TYPE_NAME_CLUB_SITE
        );

        setState({
          ...state,
          practiceGroupTypeId: practiceGroupType?.groupTypeId || DEFAULT_ID,
          siteGroupTypeId: siteGroupType?.groupTypeId || DEFAULT_ID,
        });
      } else if (orgGroupTypesState.isArrayLoaded === false) {
        getOrgGroupTypes();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgGroupTypesState]);

  function createFilterObj(formState) {
    return {
      competitionGenderTypeId:
        formState.competitionGenderTypeId > 0
          ? formState.competitionGenderTypeId
          : undefined,
      ageStart:
        formState.minAge.trim() !== '' ? formState.minAge.trim() : undefined,
      ageEnd:
        formState.maxAge.trim() !== '' ? formState.maxAge.trim() : undefined,
      firstName:
        formState.firstOrPreferredName.trim() !== ''
          ? formState.firstOrPreferredName.trim()
          : undefined,
      lastName:
        formState.lastName.trim() !== ''
          ? formState.lastName.trim()
          : undefined,
      practiceOrgGroupId:
        formState.practiceGroupId > 0 ? formState.practiceGroupId : undefined,
      clubSiteOrgGroupId:
        formState.siteGroupId > 0 ? formState.siteGroupId : undefined,
    };
  }

  return {
    isSaving: clubRosterVendorSyncState.isSaving || clubRosterTransferState.isSaving,
    isLoading: baseUsasClubState.isObjLoading || orgGroupTypesState.isArrayLoading || clubAthleteRosterState.isArrayLoading,
    isGridLoading: clubAthleteRosterState.isArrayLoading,
    state,
    clubRosterVendorSyncState,
    clubObj: baseUsasClubState.objData,
    clubAddressObj: baseUsasClubState.objData?.usasClubAddress.length > 0
      ? baseUsasClubState.objData?.usasClubAddress[0] : {},
    clubRosterArray: clubAthleteRosterState.arrayData,
    practiceGroupTypeId: state.practiceGroupTypeId,
    siteGroupTypeId: state.siteGroupTypeId,
    onEditAthlete,
    onSubmitAthleteFilter,
    onSubmitAthleteSync,
    onSubmitAthleteTransfer
  };
};

export default useClubAthleteRoster;
